import React from 'react';
import { CheckCircle as CheckCircleIcon, Close as CloseIcon, CloudSync as CloudSyncIcon, Cancel as XCircleIcon } from '@mui/icons-material';
import Button from '../ui/Button';
import type { SyncState } from '../../hooks/useKbAuditLiveSync';

interface Props {
  state: SyncState;
  onCheck: () => void;
  onDismiss: () => void;
}

const KbAuditLiveSyncNotice: React.FC<Props> = ({ state, onCheck, onDismiss }) => {
  const isLive = state.kind === 'live';
  const isError = state.kind === 'error';
  const title = isLive ? 'Changes are live for calls.' :
    state.kind === 'delayed' ? 'Live guidance is taking longer than expected.' :
    state.kind === 'checking' ? 'Checking live guidance...' :
    isError ? 'Live guidance needs attention.' :
    'Changes saved. Live guidance is updating.';

  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-live="polite"
      className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-control border p-4 ${
        isLive ? 'border-success bg-success-tint' :
        isError ? 'border-error bg-error-tint' :
        'border-info bg-info-tint'
      }`}
    >
      <div className="flex items-start gap-3">
        {isLive
          ? <CheckCircleIcon className="text-success" fontSize="small" />
          : isError
            ? <XCircleIcon className="text-error" fontSize="small" />
            : <CloudSyncIcon className="text-info" fontSize="small" />}
        <div>
          <p className="text-sm font-medium text-ink">{title}</p>
          {!isLive && <p className="mt-1 text-sm text-ink-secondary">{state.message || 'You can keep working while the update finishes.'}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!isLive && (
          <Button variant="secondary" size="sm" onClick={onCheck} disabled={state.kind === 'checking'}>
            {state.kind === 'checking' ? 'Checking...' : 'Check live status'}
          </Button>
        )}
        <button type="button" onClick={onDismiss} aria-label="Dismiss live guidance status" className="rounded-control p-2 text-ink-muted hover:bg-surface-subtle hover:text-ink focus:outline-none focus:ring-2 focus:ring-signature">
          <CloseIcon fontSize="small" />
        </button>
      </div>
    </div>
  );
};

export default KbAuditLiveSyncNotice;
